// PendingNotes — chronological list of notes attached to a pending booking,
// plus an "Add a note" textarea + submit button. Mirrors the legacy inline
// notes panel.
//
// Loading: useQuery (staleTime 0) so notes always appear fresh after a
// follow-up / final-notice / cancel mutation invalidates the key.
// Add: useMutation; clears the textarea on success and invalidates the
// notes query so the new entry appears at the top of the list.

import { useState } from 'react';
import axios from 'axios';
import { Spinner } from '../../../components/ui/Spinner/Spinner';
import { Button } from '../../../components/ui/Button/Button';
import { Textarea } from '../../../components/form/Textarea';
import { useToast } from '../../../hooks/useToast';
import { useAddPendingNote, usePendingNotes } from '../hooks/usePending';
import styles from './PendingNotes.module.css';

export interface PendingNotesProps {
  encId: string | undefined;
}

const MIN = 5;
const MAX = 5000;

function formatTimestamp(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function PendingNotes({ encId }: PendingNotesProps): JSX.Element {
  const toast = useToast();
  const notes = usePendingNotes(encId);
  const addNote = useAddPendingNote(encId);
  const [draft, setDraft] = useState('');

  const canSubmit =
    draft.trim().length >= MIN &&
    draft.length <= MAX &&
    encId !== undefined &&
    !addNote.isPending;

  async function handleAdd() {
    if (!canSubmit) return;
    try {
      await addNote.mutateAsync({ note: draft.trim() });
      setDraft('');
      toast.success('Note added.');
    } catch (err) {
      const msg = axios.isAxiosError(err)
        ? (err.response?.data as { message?: string } | undefined)?.message ??
          'Could not add note.'
        : 'Could not add note.';
      toast.error(msg);
    }
  }

  // Newest-first display order so recent activity is visible without
  // scrolling. The server returns ASC for chronological audit; the page
  // reverses for display.
  const sorted =
    notes.data !== undefined ? [...notes.data].reverse() : [];

  return (
    <section className={styles.section} id="notes" aria-label="Pending booking notes">
      <header className={styles.header}>
        <h3 className={styles.title}>
          Notes {notes.data ? <span className={styles.count}>({notes.data.length})</span> : null}
        </h3>
      </header>

      {notes.isLoading ? (
        <div className={styles.loading}>
          <Spinner size="sm" />
          <span>Loading notes…</span>
        </div>
      ) : sorted.length === 0 ? (
        <p className={styles.empty}>No notes yet for this booking.</p>
      ) : (
        <ul className={styles.list}>
          {sorted.map((n, idx) => (
            <li key={`${n.created_at}-${idx}`} className={styles.item}>
              <p className={styles.body}>{n.note}</p>
              <p className={styles.meta}>
                <span>{n.created_by_name ?? 'Unknown'}</span>
                <span aria-hidden> · </span>
                <span>{formatTimestamp(n.created_at)}</span>
              </p>
            </li>
          ))}
        </ul>
      )}

      <div className={styles.add}>
        <label className={styles.addLabel} htmlFor="add-pending-note">
          Add a note
        </label>
        <Textarea
          id="add-pending-note"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          rows={3}
          autoResize
          placeholder="Add a new note about this pending booking…"
          disabled={addNote.isPending || encId === undefined}
        />
        <div className={styles.addRow}>
          <span
            className={
              draft.length > MAX ? styles.counterError : styles.counter
            }
          >
            {draft.length} / {MAX}
          </span>
          <Button
            variant="primary"
            size="sm"
            onClick={handleAdd}
            disabled={!canSubmit}
            loading={addNote.isPending}
          >
            Add Note
          </Button>
        </div>
      </div>
    </section>
  );
}
